import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { useSession } from '../../lib/session';
import { NAV_ITEMS } from '../app-shell';
import type { SearchProductCard } from '../inventory/types';

interface PaletteResult {
  readonly key: string;
  readonly kind: 'page' | 'action' | 'product';
  readonly label: string;
  readonly hint?: string;
  readonly onSelect: () => void;
}

/** 有真实专属入口（/orders/new、/products/new）的动作——没有专属 URL 的作业（入库/出库/盘点）留给"页面"结果本身，见文件顶部范围说明 */
const STATIC_ACTIONS: ReadonlyArray<{ labelKey: string; to: string }> = [
  { labelKey: 'commandPalette.actionNewOrder', to: '/orders/new' },
  { labelKey: 'commandPalette.actionNewProduct', to: '/products/new' },
];

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * 命令面板（UX 设计书 §3.1 可发现性："⌘/Ctrl+K 命令面板可达任意功能"，§8）。
 * 范围（M11 UX 打磨，刻意narrowed）：页面导航 + 产品/批次/位置（复用既有
 * /search 端点）+ 少量有专属入口的新建动作。供应商与单据号搜索**没有**并入
 * 面板——各自的列表页已有筛选，把它们塞进 globalSearch() 需要扩后端，这次
 * 没做，留作后续。
 */
export function CommandPalette({
  open,
  onClose,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQuery = useDebounced(query, 200);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // 面板刚挂载，等下一帧再聚焦，避免触发它自己的 Esc/⌘K 监听时机问题
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const productSearch = useQuery({
    queryKey: ['command-palette-search', debouncedQuery],
    queryFn: () =>
      apiFetch<{ items: SearchProductCard[] }>(`/search?q=${encodeURIComponent(debouncedQuery)}`),
    enabled: open && debouncedQuery.trim().length >= 2,
  });

  const results = useMemo<PaletteResult[]>(() => {
    const q = query.trim().toLowerCase();
    const pageResults: PaletteResult[] = NAV_ITEMS.filter(
      (item) => !item.permission || (session?.permissions.includes(item.permission) ?? false),
    )
      .map((item) => ({ item, label: t(item.labelKey) }))
      .filter(({ label }) => q.length === 0 || label.toLowerCase().includes(q))
      .map(({ item, label }) => ({
        key: `page:${item.to}`,
        kind: 'page' as const,
        label,
        onSelect: () => navigate(item.to),
      }));

    const actionResults: PaletteResult[] = STATIC_ACTIONS.map((a) => ({ a, label: t(a.labelKey) }))
      .filter(({ label }) => q.length > 0 && label.toLowerCase().includes(q))
      .map(({ a, label }) => ({
        key: `action:${a.to}`,
        kind: 'action' as const,
        label,
        onSelect: () => navigate(a.to),
      }));

    const productResults: PaletteResult[] =
      debouncedQuery.trim().length >= 2 && productSearch.data
        ? productSearch.data.items.slice(0, 8).map((p) => ({
            key: `product:${p.productId}`,
            kind: 'product' as const,
            label: `${p.name} (${p.sku})`,
            hint: t('commandPalette.hintAvailable', { qty: p.availableBase, unit: p.baseUnitCode }),
            onSelect: () => navigate(`/products/${p.productId}`),
          }))
        : [];

    return [...actionResults, ...pageResults, ...productResults];
  }, [query, debouncedQuery, session, productSearch.data, t, navigate]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [results.length]);

  if (!open) return null;

  const select = (result: PaletteResult): void => {
    result.onSelect();
    onClose();
  };

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const chosen = results[selectedIndex];
          if (chosen) select(chosen);
        }
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={t('commandPalette.title')}
        style={{ maxWidth: '560px', width: '90vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          className="field"
          style={{ width: '100%', fontSize: 'var(--text-lg)' }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('commandPalette.placeholder')}
          aria-label={t('commandPalette.title')}
        />
        <div role="listbox" style={{ marginTop: 'var(--space-3)', maxHeight: '360px', overflowY: 'auto' }}>
          {results.length === 0 ? (
            <p className="field-hint">{t('commandPalette.empty')}</p>
          ) : (
            results.map((r, i) => (
              <button
                key={r.key}
                type="button"
                role="option"
                aria-selected={i === selectedIndex}
                className="tree__row"
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: i === selectedIndex ? 'var(--color-surface-sunken))' : 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 'var(--space-2)',
                }}
                onMouseEnter={() => setSelectedIndex(i)}
                onClick={() => select(r)}
              >
                <span>
                  <span className="badge" style={{ marginRight: 'var(--space-2)' }}>
                    {t(`commandPalette.kind.${r.kind}`)}
                  </span>
                  {r.label}
                </span>
                {r.hint && <span className="field-hint">{r.hint}</span>}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
